import { useState } from 'react';
import { Timeoff, PaginatedResponse } from '../types/entities';
import { TimeoffFormData } from '../types/forms';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ApiError {
  message: string;
  status?: number;
}

interface TimeoffQueryParams {
  hairdresserId?: number;
  startDate?: string;
  endDate?: string;
  description?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export const useTimeoffsApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const fetchTimeoffs = async (queryParams?: TimeoffQueryParams): Promise<Timeoff[]> => {
    try {
      clearError();
      const queryString = queryParams ? new URLSearchParams(
        Object.entries(queryParams).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value.toString();
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';
      
      const url = queryString ? `/timeoffs?${queryString}` : `/timeoffs`;
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(url);
      
      const data: Timeoff[] = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const fetchTimeoff = async (id: number): Promise<Timeoff> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/timeoffs/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const createTimeoff = async (timeoffData: TimeoffFormData): Promise<Timeoff> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/timeoffs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(timeoffData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const updateTimeoff = async (id: number, timeoffData: Partial<TimeoffFormData>): Promise<Timeoff> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      
      // Remove hairdresserId from update data as it's not allowed to change hairdresser during update
      const { hairdresserId, ...updateData } = timeoffData;
      
      const response = await authenticatedFetch(`/timeoffs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const deleteTimeoff = async (id: number): Promise<void> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/timeoffs/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      throw handleApiError(err);
    }
  };

  return {
    error,
    clearError,
    fetchTimeoffs,
    fetchTimeoff,
    createTimeoff,
    updateTimeoff,
    deleteTimeoff,
  };
};
